import cx from 'classnames';
import { Student } from 'models/api-response';
import { SortDirection } from 'models/enum';
import { appHelper } from 'helpers';
import { Box, ButtonLinkIcon, InfoText } from 'components/common';
import { ELSCheckBox, ELSFlex, ELSFlexItem, ELSIcon, ELSTextBox } from 'components/common/els';
import { SEARCH_STUDENTS_LABEL } from './constants';
import './student.list.scss';

interface StudentListViewProps {
  courseName: string;
  allStudents: Student[];
  nameSearchValue: string;
  isAllStudentsChecked: boolean;
  showStudentList: boolean;
  sortDirection: SortDirection;
  onShowStudentList: Function;
  onSearchNameChange: Function;
  onStudentSort: Function;
  onAllStudentsChange: Function;
  onStudentChange: Function;
}

const StudentListView = (props: StudentListViewProps) => {
  const allStudentsCheckboxId = 'checkbox-all-students';
  const { courseName, allStudents, showStudentList, nameSearchValue, isAllStudentsChecked, sortDirection } = props;
  const { onShowStudentList, onSearchNameChange, onStudentSort, onAllStudentsChange, onStudentChange } = props;
  const studentSelectedCount = allStudents.filter((item) => item.selected).length;
  const studentSelectedText = appHelper.toSinglePlural(studentSelectedCount, `1 student selected`, `${studentSelectedCount} students selected`);
  const showStudentListIcon = showStudentList ? 'chevron-up' : 'chevron-down';
  const sortStudentListIcon = sortDirection === SortDirection.ASC ? 'sorting-up2' : 'sorting-down';
  const isAscending = sortDirection === SortDirection.ASC;
  const sortStudentsAriaLabel = `Sort students by name, currently sorted ${isAscending ? 'ascending' : 'descending'}, activate to sort ${isAscending ? 'descending' : 'ascending'}`;
  const shouldDisabledAllOptions = allStudents.some((item) => item.isDisabledSelect);

  return (
    <div className="student-list">
      <ELSFlex middle spaceBetween>
        <ELSFlexItem>
          <span className="u-els-bold u-els-font-size-h4">Select Students</span>
          <div className="u-els-display-inline-block u-els-padding-left-1o2">
            <ButtonLinkIcon
              text={studentSelectedText}
              iconName={showStudentListIcon}
              iconPosition="right"
              iconSize="3o4"
              onClick={onShowStudentList}
              preventAutoPending
              ariaLabel={studentSelectedText}
              ariaExpanded={showStudentList}
            />
          </div>
        </ELSFlexItem>
        <ELSFlexItem>
          <InfoText text='New students will also get assigned if "all students" are selected.' />
        </ELSFlexItem>
      </ELSFlex>

      {showStudentList && (
        <Box pl2>
          <Box mt>
            <ELSIcon prefix="gizmo" name="institution" align="sub" />
            <span className="u-els-padding-left u-els-font-size-h4">{courseName}</span>
          </Box>
          <Box mt className="u-els-width-1o2">
            <ELSTextBox iconLeft="search" iconLeftPrefix="gizmo" placeholder="Search students" value={nameSearchValue} changeHandler={onSearchNameChange}>
              <span className="u-els-hide-visually">{SEARCH_STUDENTS_LABEL}</span>
            </ELSTextBox>
          </Box>
          <Box mt2 className="student-list__table">
            <ELSFlex left className="student-list__table-header">
              <ELSFlexItem>
                {/* Associated via htmlFor; airbnb's assert: "both" also demands nesting, which HTML does not */}
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor={`field-input-${allStudentsCheckboxId}`} className="u-els-hide-visually">
                  All Students
                </label>
                <ELSCheckBox id={allStudentsCheckboxId} isDisabled={shouldDisabledAllOptions} checked={isAllStudentsChecked} changeHandler={onAllStudentsChange} />
              </ELSFlexItem>
              <ELSFlexItem>
                <ButtonLinkIcon
                  text="All Students"
                  iconName={sortStudentListIcon}
                  iconPosition="right"
                  iconSize="3o4"
                  onClick={onStudentSort}
                  preventAutoPending
                  ariaLabel={sortStudentsAriaLabel}
                />
              </ELSFlexItem>
            </ELSFlex>
            {allStudents.map((student) => {
              const studentName = `${student.firstName} ${student.lastName}`.trim();
              const studentCheckboxId = `checkbox-student-${student.id}`;

              return (
                <ELSFlex key={String(student.id)} left className={cx('student-list__row u-els-padding', { 'u-els-display-none': !student.isShown })}>
                  <ELSFlexItem>
                    <label htmlFor={`field-input-${studentCheckboxId}`} className="u-els-hide-visually">
                      {studentName}
                    </label>
                    <ELSCheckBox
                      id={studentCheckboxId}
                      isDisabled={student.isDisabledSelect}
                      checked={student.selected}
                      changeHandler={(event, checked) => onStudentChange(student.id, checked)}
                    />
                  </ELSFlexItem>
                  <ELSFlexItem>{studentName}</ELSFlexItem>
                </ELSFlex>
              );
            })}
          </Box>
        </Box>
      )}
    </div>
  );
};

StudentListView.displayName = 'StudentListView';
export default StudentListView;
